import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, ArrowLeft, ShieldCheck, CheckCircle2, RotateCcw } from "lucide-react";

const STEPS = [
  {
    key: "housing",
    title: "What's your current housing situation?",
    hint: "This helps us understand how urgently to respond.",
    options: ["Unsheltered — street, vehicle, or outdoors", "At risk of losing my housing", "Staying in an emergency shelter"],
  },
  {
    key: "branch",
    title: "Which branch did you serve in?",
    hint: "Any length of service counts.",
    options: ["Army", "Navy", "Air Force", "Marines", "Coast Guard", "Space Force"],
  },
  {
    key: "needs",
    title: "What do you need most right now?",
    hint: "You can share more later — pick the most pressing.",
    options: ["Housing", "Employment", "Legal / VA Benefits", "Recovery Support"],
  },
];

export const IntakeQuizModal = ({ open, onClose, onComplete }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const isResult = step >= STEPS.length;

  const reset = () => { setStep(0); setAnswers({}); };
  const close = () => { onClose(); setTimeout(reset, 300); };

  const select = (key, val) => {
    setAnswers((a) => ({ ...a, [key]: val }));
    setTimeout(() => setStep((s) => s + 1), 160);
  };

  const current = STEPS[step];
  const progress = Math.min(step, STEPS.length) / STEPS.length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="screener-modal"
        >
          <div className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm" onClick={close} />
          <motion.div
            className="relative w-full sm:max-w-lg bg-offwhite rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl"
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Header */}
            <div className="bg-navy text-offwhite px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="h-5 w-5 text-gold" />
                <span className="font-heading font-bold">Quick Eligibility Screener</span>
              </div>
              <button onClick={close} data-testid="screener-close" aria-label="Close" className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-offwhite/10 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress */}
            <div className="h-1 bg-softgray">
              <motion.div className="h-full bg-gold" animate={{ width: `${(isResult ? 1 : progress) * 100}%` }} transition={{ duration: 0.4 }} />
            </div>

            <div className="p-6 md:p-8 min-h-[340px] flex flex-col">
              {!isResult ? (
                <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="flex-1">
                  <div className="label-caps text-slate">Step {step + 1} of {STEPS.length}</div>
                  <h3 className="mt-2 font-heading text-2xl font-black text-navy leading-tight">{current.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/70">{current.hint}</p>

                  <div className="mt-6 grid grid-cols-1 gap-3">
                    {current.options.map((opt) => {
                      const active = answers[current.key] === opt;
                      return (
                        <button
                          key={opt}
                          onClick={() => select(current.key, opt)}
                          data-testid={`screener-opt-${current.key}-${opt.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                          className={`group flex items-center justify-between text-left rounded-md border px-5 py-4 text-sm font-semibold transition-colors ${
                            active ? "border-navy bg-navy text-offwhite" : "border-softgray bg-white text-charcoal hover:border-navy"
                          }`}
                        >
                          {opt}
                          <ArrowRight className={`h-4 w-4 transition-transform group-hover:translate-x-1 ${active ? "text-gold" : "text-slate"}`} />
                        </button>
                      );
                    })}
                  </div>

                  {step > 0 && (
                    <button onClick={() => setStep((s) => s - 1)} data-testid="screener-back" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold-deep transition-colors">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                  )}
                </motion.div>
              ) : (
                <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex-1 flex flex-col" data-testid="screener-result">
                  <div className="h-12 w-12 rounded-md bg-teal/10 flex items-center justify-center">
                    <CheckCircle2 className="h-7 w-7 text-teal" />
                  </div>
                  <h3 className="mt-5 font-heading text-2xl font-black text-navy leading-tight">You may be eligible for Iron Shield.</h3>
                  <p className="mt-3 text-charcoal/80 leading-relaxed">
                    Based on your answers, you look like a good fit for our transitional housing and reset support. The next step is a short intake form — we've pre-filled what you told us to save you time.
                  </p>

                  <div className="mt-5 rounded-md border border-softgray bg-white p-4 text-sm">
                    <div className="label-caps text-navy mb-2">Your answers</div>
                    <ul className="space-y-1 text-charcoal/80">
                      <li>• Housing: <span className="font-semibold">{answers.housing}</span></li>
                      <li>• Branch: <span className="font-semibold">{answers.branch}</span></li>
                      <li>• Top need: <span className="font-semibold">{answers.needs}</span></li>
                    </ul>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => { onComplete(answers); close(); }}
                      data-testid="screener-complete"
                      className="group inline-flex items-center justify-center gap-2 bg-navy text-offwhite px-6 py-4 rounded-md text-sm font-bold uppercase tracking-wide hover:bg-navy-deep transition-colors"
                    >
                      Complete Full Intake Form <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </button>
                    <button onClick={reset} data-testid="screener-restart" className="inline-flex items-center justify-center gap-2 border border-softgray text-navy px-6 py-4 rounded-md text-sm font-semibold hover:border-navy transition-colors">
                      <RotateCcw className="h-4 w-4" /> Restart
                    </button>
                  </div>
                  <p className="mt-4 text-xs text-slate">Not eligible or unsure? Call the Intake Hotline — we'll point you to the right resource either way.</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Maps screener answers -> intake form prefill values
export const screenerToPrefill = (answers) => {
  const housingMap = {
    "Unsheltered — street, vehicle, or outdoors": { risk: "Yes", text: "Currently unsheltered — staying on the street, in a vehicle, or outdoors." },
    "At risk of losing my housing": { risk: "At risk", text: "At risk of losing my current housing." },
    "Staying in an emergency shelter": { risk: "Yes", text: "Currently staying in an emergency shelter." },
  };
  const needMap = {
    "Housing": "Emergency shelter",
    "Employment": "Employment help",
    "Legal / VA Benefits": "Benefits / VA navigation",
    "Recovery Support": "Substance recovery support",
  };
  const h = housingMap[answers.housing] || {};
  return {
    veteran_status: "Veteran",
    homeless_or_at_risk: h.risk || "",
    housing_situation: h.text || "",
    immediate_needs: needMap[answers.needs] ? [needMap[answers.needs]] : [],
  };
};

export default IntakeQuizModal;
